import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  InputBase,
  Stack,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { SoapImprovementCard } from '@/apps/latest/components/molecules/SoapImprovementCard'
import { type SoapImprovementSuggestion } from '@/apps/latest/features/formatter/types/soapImprovement'
import { useSendChatMessageMutation } from '@/features/formatter'
import { useToast } from '@/shared/contexts/ToastContext'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'
import { type SoapContent } from '@/shared/types/yakurekiSoap'

interface SoapInlineAIProps {
  yakurekiId: string
  // 反映できたら、返ってきた SOAP を渡す (読み直しと変わった行の印は呼び出し側)
  onSoapUpdate: (updatedSoap: SoapContent) => Promise<void>
  // AI の改善提案 (back が規則で作る定型の指示。押すと「AI で改善」と同じ差分更新に送られる = 同じ機械なので同じ口)。
  // 渡すと placeholder に「· 提案 N 件」(N = まだ押していない数)、開いた欄の下に提案の行 (hub log/108 決定 43)
  suggestions?: SoapImprovementSuggestion[]
  onApplySuggestion?: (suggestion: SoapImprovementSuggestion) => Promise<void>
  // 店の設定「改善提案を最初から開く」= 押していない提案があれば提案の行を開いて始める
  defaultOpen?: boolean
}

// placeholder は欄の focus で切り替える (提案の行の開閉ではない)。focus が無いときは例を入れず、押していない
// 提案があれば件数。focus 中は例を添え、件数は出さない (提案の行が下に見えている。両方付けるとスマホ幅の
// 1 行 26 字を超える。hub log/108 決定 50)
export const PLACEHOLDER_REST = 'AI にどう直すか書く…'
export const PLACEHOLDER_FOCUSED = 'AI にどう直すか書く (例: S を簡潔に)'
export const placeholderWithCount = (n: number) =>
  `AI にどう直すか書く · 提案 ${n} 件`

// SOAP の行と同じ字格 (body2。文字拡大に追従)・行の高さ
const LINE_HEIGHT_RATIO = 1.43

// 文書を直す AI への口 (design-book 02-components/text-field.md (2c)・ADR-0055)。閉じた行は持たず、行そのものが
// 空の 1 行の欄: placeholder は focus が無ければ「AI にどう直すか書く…」(押していない提案があれば
// 「AI にどう直すか書く · 提案 N 件」)、focus 中は「AI にどう直すか書く (例: S を簡潔に)」。focus で欄の下に提案の行が開く。送るとその場で SOAP を差分更新して空に戻す
// (modal / chat panel は無い)。枠は透明で hover と focus で bg.subtle、focus で枠 = SOAP の行の inline 編集と同じ
// 「打てる場所」の服 (決定 43〜45 の閉じた行は ActionLink と同じ「押す物」の服で、打てる場所に見えなかった。
// hub log/108 決定 46)。
// 提案の行を畳むのは Esc と、入力が空で送信中でなければ欄の外を押したとき (提案の有無に関わらず。決定 44)。
// 資料はここでは扱わない。資料を SOAP に入れるのは下の「資料」の節の ⓘ。
export const SoapInlineAI: FC<SoapInlineAIProps> = ({
  yakurekiId,
  onSoapUpdate,
  suggestions = [],
  onApplySuggestion,
  defaultOpen = false,
}) => {
  const { showToast } = useToast()
  const pendingCount = suggestions.filter((s) => s.status === 'pending').length
  // open = 提案の行が開いている (欄そのものは常にある)。
  // 設定「最初から開く」は、押していない提案があれば開いて始める。最初から提案があればそのまま開き、
  // 後から届いたら (提案は polling で遅れて届く) そのとき 1 回だけ開く。
  // 「値の変化時だけ state を調整する」React 公式パターン (render 中の guarded setState)
  const wantOpen = defaultOpen && pendingCount > 0
  const [open, setOpen] = useState(wantOpen)
  const [prompt, setPrompt] = useState('')
  const [failed, setFailed] = useState(false)
  // 欄に focus があるか (placeholder の切り替えに使う。提案の行の開閉 open とは別)
  const [focused, setFocused] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const sendMessage = useSendChatMessageMutation()
  const isBusy = sendMessage.isPending

  const [syncedWantOpen, setSyncedWantOpen] = useState(wantOpen)
  if (wantOpen !== syncedWantOpen) {
    setSyncedWantOpen(wantOpen)
    // 設定で開くときは page の focus を奪わない (欄に focus しない)
    if (wantOpen) setOpen(true)
  }

  // 欄の外を押したら提案の行を畳む (打ちかけの文章があるか送信中なら畳まない)。入力の blur でなく document の
  // pointer で見る: 設定で開いて始まったときは入力に focus が無く blur が来ない。提案の行は欄の中なので
  // 押しても畳まない。Esc の無いスマホ・タブレットでも畳める (hub log/108 決定 44)
  const canClose = open && !prompt.trim() && !isBusy
  useEffect(() => {
    if (!canClose) return
    const onPress = (e: Event) => {
      const target = e.target
      if (target instanceof Node && rootRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPress)
    document.addEventListener('touchstart', onPress)
    return () => {
      document.removeEventListener('mousedown', onPress)
      document.removeEventListener('touchstart', onPress)
    }
  }, [canClose])

  // 畳んで focus も外す (placeholder が「AI で改善…」に戻る)
  const close = () => {
    setOpen(false)
    inputRef.current?.blur()
  }

  const reset = () => {
    setPrompt('')
    setFailed(false)
    close()
  }

  const submit = async () => {
    const text = prompt.trim()
    if (!text || isBusy) return
    setFailed(false)
    try {
      const res = await sendMessage.mutateAsync({
        yakurekiId,
        message: text,
        inputType: 'text',
      })
      await onSoapUpdate(res.updated_soap)
      showToast('AI で改善しました', 'success')
      reset()
    } catch {
      // 欄は畳まず、文章を残す
      setFailed(true)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
    if (e.key === 'Escape' && !isBusy) {
      reset()
    }
  }

  // placeholder は動詞で終わる指示の文 (機能名「AI で改善…」は何を書くか言わず、タッチには hover も
  // I ビームも無いので入力欄と読めなかった)。既存の placeholder の語調 (「指導メモを書く」) に揃え、スマホ幅で
  // 1 行 (26 字まで)。機能名は aria-label と toast が持つ (hub log/108 決定 48・50)
  const placeholder = focused
    ? PLACEHOLDER_FOCUSED
    : pendingCount > 0
    ? placeholderWithCount(pendingCount)
    : PLACEHOLDER_REST

  return (
    <Box ref={rootRef}>
      {/* 欄 = text-field.md (2b) の inline 1 行 field。枠は透明、hover と focus で bg.subtle、focus で枠。
          高さは SOAP の行と同じ 1 行 (20px) に上下 5px と枠 1px で 32。3 行まで伸びる (✦ は 1 行目の高さに中央寄せ)。
          hover の地だけ左右 4px 食み出す (SOAP の見出し CopyableHeading と同じ)。
          ✦ 16px + 6px = 22px = SOAP の行頭の列 (SOAP_LINE_BULLET_WIDTH) で、文字が本文の列に揃う */}
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
          minHeight: 32,
          px: 0.5,
          py: '5px',
          mx: -0.5,
          borderRadius: `${RADIUS.sm}px`,
          border: '1px solid transparent',
          cursor: 'text',
          transition: 'background-color 120ms, border-color 120ms',
          '&:hover, &:focus-within': { bgcolor: colorTokens.bg.subtle },
          '&:focus-within': { borderColor: colorTokens.border.default },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            height: (theme) =>
              `calc(${theme.typography.body2.fontSize} * ${LINE_HEIGHT_RATIO})`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 16, color: colorTokens.fg.muted }} />
        </Box>
        <InputBase
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={3}
          value={prompt}
          disabled={isBusy}
          placeholder={placeholder}
          inputProps={{ 'aria-label': 'AI で改善' }}
          onFocus={() => {
            setFocused(true)
            setOpen(true)
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            p: 0,
            fontSize: (theme) => theme.typography.body2.fontSize,
            lineHeight: LINE_HEIGHT_RATIO,
            color: colorTokens.fg.default,
            '& textarea': { p: 0 },
            '& textarea::placeholder': {
              color: colorTokens.fg.faint,
              opacity: 1,
            },
          }}
        />
        {isBusy && (
          <CircularProgress
            size={14}
            thickness={4}
            sx={{ color: colorTokens.fg.muted, flexShrink: 0, mt: 0.375 }}
          />
        )}
      </Box>
      {open && suggestions.length > 0 && (
        // 提案の行 = 欄の下 (Notion の Ask AI の「Suggested」と同じ)。押しても入力の focus を奪わない。
        // 行の hover の地も欄と同じだけ食み出し、「・」は ✦ と同じ列
        <Stack
          spacing={0.25}
          sx={{ mt: 0.5, mx: -0.5 }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((s) => (
            <SoapImprovementCard
              key={s.id}
              suggestion={s}
              onExecute={onApplySuggestion}
            />
          ))}
        </Stack>
      )}
      {failed && (
        <Typography
          sx={{ ...typeScale.label, color: colorTokens.fg.muted, mt: 0.5 }}
        >
          反映できませんでした
        </Typography>
      )}
    </Box>
  )
}
